/**
 * A topic reference in skill-body text is a `[[…]]`-bracketed token naming a
 * topic the procedure investigates — e.g. `[[/imu/data]]` or
 * `[[vehicle_attitude]]` — optionally followed by a dotted field path
 * (`[[/imu/data.linear_acceleration.x]]`). The topic *name* (without the
 * brackets or field suffix) is what a skill stores in `relevant_topics`; the
 * field suffix is only prose in the body. Because topic names may themselves
 * contain dots (proto-style `/proto/executive.extended_status/context`), the
 * name/field boundary is resolved against the org catalog when possible — see
 * {@link splitReferenceName}.
 *
 * The brackets make recognition explicit rather than inferred: topic names in
 * the wild may be ROS-style slash paths (`/imu/data`) or bare words
 * (`vehicle_attitude`, `log`), and a bare word is indistinguishable from prose
 * without a marker. Recognition is grammar-driven, not catalog-driven: any
 * well-formed `[[name]]` counts as a reference whether or not the name is a
 * known org topic, so an author can reference data that does not exist yet.
 * The org catalog is advisory — it only decides whether a reference is styled
 * as known or not-yet-known (see {@link TopicReferenceMatch.known}), never
 * whether it counts as a reference.
 */

/**
 * A complete bracketed reference: `[[name]]` or `[[name.field.path]]`. The
 * inner text is one run of topic/field characters — the alphabet observed
 * across every topic name and message path in the metastore (letters, digits,
 * `_`, `-`, `/`, `.`). It excludes whitespace and brackets, so an unclosed
 * `[[` or a bracketed phrase of prose never matches. Topic names may
 * themselves contain dots (proto-style, e.g.
 * `/proto/executive.extended_status/context`), so where the name ends and the
 * field path begins is decided by {@link splitReferenceName}, not the regex.
 */
const TOPIC_REFERENCE = /\[\[([A-Za-z0-9_./-]+)\]\]/g;

/**
 * Index in ``inner`` where a field path could start absent catalog knowledge:
 * the first `.` after the last `/` (for a bare name, the first `.` overall).
 * A dot *inside* a path segment (`/proto/foo.bar/baz`) never starts a field —
 * fields are dotted suffixes, and metastore names show dots mid-path.
 * Returns -1 when the whole inner text is the name. A leading dot (index 0)
 * is treated as part of the name rather than an empty name.
 */
function fallbackFieldStart(inner: string): number {
  const dot = inner.indexOf(".", inner.lastIndexOf("/") + 1);
  return dot > 0 ? dot : -1;
}

/**
 * Split a reference's inner text into the topic name and the index where its
 * `.field` suffix starts (-1 for none). Catalog-aware: the longest known
 * topic name that prefixes ``inner`` at a `.` boundary (or exactly) wins —
 * required because names may contain dots, making the split ambiguous by
 * grammar alone. Falls back to {@link fallbackFieldStart} for names not in
 * the catalog, preserving "authoring ahead of the data".
 */
function splitReferenceName(
  inner: string,
  knownTopicNames: readonly string[],
): { name: string; fieldStart: number } {
  let best: string | null = null;
  for (const known of knownTopicNames) {
    if (
      inner.startsWith(known) &&
      (inner.length === known.length || inner.charAt(known.length) === ".") &&
      (best === null || known.length > best.length)
    ) {
      best = known;
    }
  }
  if (best !== null) {
    return {
      name: best,
      fieldStart: inner.length === best.length ? -1 : best.length,
    };
  }
  const dot = fallbackFieldStart(inner);
  return { name: dot === -1 ? inner : inner.slice(0, dot), fieldStart: dot };
}

/**
 * Characters that may appear inside an in-progress `[[…` token (topic-name
 * characters plus the `.` field separator). Typing anything else closes the
 * token — see {@link activeTopicTokenAtCursor} and the editor's hop-out rule.
 */
export const TOPIC_TOKEN_CHAR = /[A-Za-z0-9_./-]/;

/**
 * The `[[topic[.field]` token the caret is currently inside, or `null` when
 * the caret is not within one. A token runs forward from an opening `[[` to
 * the caret and is still "in progress" as long as no whitespace or bracket has
 * interrupted it (a closing `]]` inserted by autoclose may sit *after* the
 * caret without ending the token).
 */
export interface ActiveTopicToken {
  /** Offset of the opening `[[`. Replace from here to the caret on accept. */
  tokenStart: number;
  /** Text after the `[[` and up to the caret (may include a `.field` part). */
  query: string;
  /**
   * Exclusive end of the token: the caret itself, or — when the caret is
   * parked immediately before an autoclosed `]]` — the offset past that `]]`.
   * Accept replaces `[tokenStart, tokenEnd)`, so re-accepting a topic or
   * drilling a field into an already-closed token reuses the existing `]]`
   * instead of appending a second pair.
   */
  tokenEnd: number;
}

/**
 * Detect an in-progress `[[topic` token at ``cursor`` for autocomplete.
 *
 * Finds the nearest `[[` fully before the caret and returns it as the active
 * token unless the text between the brackets and the caret contains
 * whitespace or another bracket — either means the caret has left the token
 * (prose resumed, or a previous reference already closed).
 */
export function activeTopicTokenAtCursor(
  text: string,
  cursor: number,
): ActiveTopicToken | null {
  const tokenStart = text.lastIndexOf("[[", cursor - 2);
  if (tokenStart === -1 || tokenStart + 2 > cursor) {
    return null;
  }
  const query = text.slice(tokenStart + 2, cursor);
  if (/[\s[\]]/.test(query)) {
    return null;
  }
  // An autoclosed `]]` sitting right after the caret belongs to this token; its
  // end runs past that pair so accept overwrites it rather than duplicating it.
  const tokenEnd = text.startsWith("]]", cursor) ? cursor + 2 : cursor;
  return { tokenStart, query, tokenEnd };
}

/**
 * Split an active-token query into its topic portion and the field fragment
 * being typed. While the topic part is undecided the user is choosing a
 * topic (``fieldFragment: null``); once a known topic name is followed by a
 * `.` they are drilling into its field paths.
 *
 * Catalog-aware, because topic names may contain dots: a known name that
 * prefixes the query at a `.` boundary flips to field mode (longest wins),
 * but a query that is still a substring of some dotted catalog name stays in
 * topic mode — flipping there would strand the menu on a topic that doesn't
 * exist (e.g. typing `executive.ext` toward
 * `/proto/executive.extended_status/context`). Only then does the grammar
 * fallback split apply (same rule as {@link findTopicReferences} uses for
 * unknown names).
 *
 * `"imu/da"` → `{ topicPart: "imu/da", fieldFragment: null }`
 * `"imu/data.lin"` → `{ topicPart: "imu/data", fieldFragment: "lin" }`
 */
export function splitTopicQuery(
  query: string,
  knownTopicNames: readonly string[],
): {
  topicPart: string;
  fieldFragment: string | null;
} {
  const { name, fieldStart } = splitReferenceName(query, knownTopicNames);
  if (fieldStart === -1) {
    return { topicPart: query, fieldFragment: null };
  }
  if (!knownTopicNames.includes(name)) {
    const q = query.toLowerCase();
    if (knownTopicNames.some((known) => known.toLowerCase().includes(q))) {
      return { topicPart: query, fieldFragment: null };
    }
  }
  return {
    topicPart: name,
    fieldFragment: query.slice(fieldStart + 1),
  };
}

/** A resolved topic reference and the character spans it occupies. */
export interface TopicReferenceMatch {
  /** Index of the opening `[[`. */
  start: number;
  /** Index of the first character of the topic name (``start + 2``). */
  nameStart: number;
  /** Exclusive end index of the topic name — where any `.field` suffix begins. */
  nameEnd: number;
  /**
   * Exclusive end index of the trailing `.field` path, or equal to ``nameEnd``
   * when the reference has no field suffix. The span ``[nameEnd, fieldEnd)``
   * (which includes the connecting `.`) is the field path written after the
   * topic.
   */
  fieldEnd: number;
  /** Exclusive end index of the whole reference, past the closing `]]`. */
  end: number;
  /** The topic name the token resolved to (brackets and field excluded). */
  name: string;
  /**
   * Whether ``name`` is a known org topic. Advisory only — it drives styling
   * (known vs not-yet-known), never whether the token counts as a reference.
   */
  known: boolean;
}

/**
 * Locate every `[[topic[.field]]]` reference in ``body``, with the character
 * spans of its parts. Recognition does not depend on ``knownTopicNames``: an
 * author may reference a topic that does not exist yet. The catalog only sets
 * each match's ``known`` flag, which callers use for styling. Used both to
 * persist ``relevant_topics`` (name only, known or not) and to highlight
 * references in the editor and the read-only markdown preview.
 */
export function findTopicReferences(
  body: string,
  knownTopicNames: readonly string[],
): TopicReferenceMatch[] {
  const known = new Set(knownTopicNames);
  const matches: TopicReferenceMatch[] = [];
  for (const match of body.matchAll(TOPIC_REFERENCE)) {
    const inner = match[1];
    const { name } = splitReferenceName(inner, knownTopicNames);
    const nameStart = match.index + 2;
    const nameEnd = nameStart + name.length;
    matches.push({
      start: match.index,
      nameStart,
      nameEnd,
      fieldEnd: nameStart + inner.length,
      end: match.index + match[0].length,
      name,
      known: known.has(name),
    });
  }
  return matches;
}

/**
 * A run of body text with a rendering role: prose, a bracket delimiter, a
 * topic-name chip (known or not-yet-known), or a field-path chip. The
 * connecting `.` between a topic and its field is emitted as ``plain`` so the
 * two read as clearly distinct but related tokens rather than one fused blob.
 */
export interface TopicReferenceSegment {
  text: string;
  kind: "plain" | "bracket" | "topic" | "topicUnknown" | "field";
}

/**
 * Split ``text`` into contiguous segments for rendering topic references —
 * the single source of segmentation for the editor's highlight overlay and
 * the markdown preview, so the two surfaces color a reference identically.
 * Concatenating the segments' ``text`` reproduces ``text`` exactly (the
 * editor overlay relies on this to stay aligned with its textarea).
 */
export function segmentTopicReferences(
  text: string,
  knownTopicNames: readonly string[],
): TopicReferenceSegment[] {
  const segments: TopicReferenceSegment[] = [];
  let cursor = 0;
  for (const match of findTopicReferences(text, knownTopicNames)) {
    if (match.start > cursor) {
      segments.push({ text: text.slice(cursor, match.start), kind: "plain" });
    }
    segments.push({ text: "[[", kind: "bracket" });
    segments.push({
      text: match.name,
      kind: match.known ? "topic" : "topicUnknown",
    });
    if (match.fieldEnd > match.nameEnd) {
      // Separator dot stays plain; the field path (after it) is its own chip.
      segments.push({ text: ".", kind: "plain" });
      segments.push({
        text: text.slice(match.nameEnd + 1, match.fieldEnd),
        kind: "field",
      });
    }
    segments.push({ text: "]]", kind: "bracket" });
    cursor = match.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), kind: "plain" });
  }
  return segments;
}

/**
 * Extract the distinct topic names referenced in ``body``, in first-appearance
 * order — including names not yet in ``knownTopicNames`` (authoring ahead of
 * the data is valid). See {@link findTopicReferences} for the recognition
 * rules; this is the deduped name-only projection persisted as
 * ``relevant_topics``.
 */
export function extractTopicReferences(
  body: string,
  knownTopicNames: readonly string[],
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const { name } of findTopicReferences(body, knownTopicNames)) {
    if (!seen.has(name)) {
      seen.add(name);
      result.push(name);
    }
  }
  return result;
}
